import { Code, Link, Text } from "@radix-ui/themes";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { detectOs, OS_COPY } from "../onboarding/setupCopy";

/** The guide is a toggle in Billing questions on Plan & billing; this is its anchor. */
export const ONE_ACCOUNT_GUIDE_ID = "one-account";
export const ONE_ACCOUNT_GUIDE_TITLE = "How to connect everything under one account";
const GUIDE_PATH = `/billing#${ONE_ACCOUNT_GUIDE_ID}`;

/**
 * The steps for moving ChatGPT and a device onto the account that has Pro. Step 1 comes first on
 * purpose: until the device is removed from the old account, the setup command silently reconnects
 * it there.
 */
export function OneAccountGuideSteps() {
    return (
        <>
            <Text as="p" size="2" color="gray">
                Do these in order, so ChatGPT and your device both use the account you want.
            </Text>
            <ol
                style={{
                    margin: "12px 0 0",
                    paddingLeft: 20,
                    display: "grid",
                    gap: 10,
                    color: "var(--gray-11)",
                }}
            >
                <li>
                    <Text size="2" color="gray">
                        Remove the device from the old account first. Sign in to this dashboard with the old email, open Devices and click Revoke (or
                        Remove) on the device. Otherwise the setup command reconnects the old account.
                    </Text>
                </li>
                <li>
                    <Text size="2" color="gray">
                        Sign out, then sign in with the email you want to use Desktop Commander with. Add the device again: run the setup command and
                        confirm with that same email on the page that opens.
                    </Text>
                </li>
                <li>
                    <Text size="2" color="gray">
                        In ChatGPT, open Settings › Apps › Remote Desktop Commander, click ••• next to the old email and choose Disconnect, then
                        connect again. Do it in the browser where you're signed in with that email, so ChatGPT picks it.
                    </Text>
                </li>
            </ol>
            <Text as="p" size="2" color="gray" mt="3">
                Can't sign in to the old account? Instead of step 1, press Ctrl+C in the terminal and run:
            </Text>
            <Code
                size="2"
                color="gray"
                style={{
                    display: "block",
                    width: "fit-content",
                    maxWidth: "100%",
                    marginTop: "var(--space-2)",
                    overflowWrap: "anywhere",
                }}
            >
                {OS_COPY[detectOs()].command} --logout
            </Code>
        </>
    );
}

/**
 * Link to the guide's toggle in Billing questions, which opens it and scrolls to it, so support can
 * send the same link.
 */
export function OneAccountGuideLink({ children = ONE_ACCOUNT_GUIDE_TITLE, onClick }: { children?: ReactNode; onClick?: () => void }) {
    return (
        <Link asChild>
            <RouterLink to={GUIDE_PATH} onClick={onClick}>
                {children}
            </RouterLink>
        </Link>
    );
}
